import type { ReactElement } from 'react';
import { Table, type ColumnData, type RowData } from '@vwpfs/mary-air-lib/components/air/06-molecules/Table';
import { Badge } from '@vwpfs/mary-air-lib/components/air/05-atoms/Badge';
import { ThemePalette, ThemePaletteState } from '@vwpfs/mary-air-lib/theme/_Types';
import { formatLastChangeNote, formatOverrideNote } from '../lib/override-note';
import type { VehicleListItem } from '../types/vehicle';
import { VehicleActions } from './VehicleActions';

interface Props {
  items: VehicleListItem[];
}

const COLUMNS: ColumnData[] = [
  { label: 'Id' },
  // KomNr sits beside the plate because it is the identifier that survives when the plate is
  // still null — a vehicle that has been ordered but not yet registered.
  { label: 'KomNr' },
  { label: 'License plate' },
  { label: 'Vehicle' },
  { label: 'Dealer' },
  { label: 'Dealer status' },
  { label: 'Mobilist status' },
  { label: 'Actions' }
];

function vehicleCell(item: VehicleListItem): ReactElement {
  const main = [item.brand, item.model].filter(Boolean).join(' ') || '—';
  const meta = [item.bodyWork, item.constructionYear].filter(Boolean).join(' · ');
  return (
    <div className="vap-vehicle-cell">
      <span className="vap-vehicle-cell__title">
        <strong>{main}</strong>
        {item.statusNew && <span className="vap-vehicle-cell__new">· new</span>}
      </span>
      {meta && <small className="vap-vehicle-cell__meta">{meta}</small>}
    </div>
  );
}

/**
 * Both status columns return the same container, even though this one only ever holds a single
 * badge. A bare Badge is inline while the Mobilist column is a flex column, and two different box
 * types in neighbouring cells do not sit on the same line - which is why "Available" and
 * "No overrides" looked off from each other.
 */
function dealerStatusBadge(item: VehicleListItem): ReactElement {
  if (item.statusSold) {
    return (
      <div className="vap-status-badges">
        <Badge key="sold" theme={{ paletteState: ThemePaletteState.DANGER }}>
          Sold
        </Badge>
      </div>
    );
  }
  if (item.statusReserved) {
    return (
      <div className="vap-status-badges">
        <Badge key="reserved" theme={{ paletteState: ThemePaletteState.WARNING }}>
          Reserved
        </Badge>
      </div>
    );
  }
  return (
    <div className="vap-status-badges">
      <Badge key="available" theme={{ palette: ThemePalette.CONTRAST_TERTIARY }}>
        Available
      </Badge>
    </div>
  );
}

/**
 * Wraps a badge so hovering it reveals why the override is there.
 *
 * The `title` goes on a wrapper because Badge takes only theme props and children — a `title`
 * passed to it would be dropped silently. See `.vap-badge-note` for the hover affordance. History
 * is the accessible path to the same information, since `title` is unavailable on touch and read
 * inconsistently by screen readers.
 */
function withNote(badge: ReactElement, note: string | undefined, key: string): ReactElement {
  if (!note) {
    return badge;
  }

  return (
    <span key={key} className="vap-badge-note" title={note}>
      {badge}
    </span>
  );
}

function mobilistStatusBadges(item: VehicleListItem): ReactElement {
  const badges: ReactElement[] = [];
  if (item.mobilistStatusReserved) {
    badges.push(
      withNote(
        <Badge key="reserved" theme={{ paletteState: ThemePaletteState.WARNING }}>
          Reserved
        </Badge>,
        formatOverrideNote(item.mobilistReserveNote),
        'reserved-note'
      )
    );
  }
  const blockedCount = item.mobilistUseForAd.filter((s) => !s.useForAd).length;
  if (blockedCount > 0) {
    badges.push(
      withNote(
        <Badge key="blocked" theme={{ paletteState: ThemePaletteState.WARNING }}>
          Ad blocked × {blockedCount}
        </Badge>,
        formatOverrideNote(item.mobilistAdBlockNote),
        'blocked-note'
      )
    );
  }
  if (badges.length === 0) {
    // A cleared override still has a reason worth reading, and no badge of its own to hang it on.
    badges.push(
      withNote(
        <Badge key="ok" theme={{ palette: ThemePalette.CONTRAST_TERTIARY }}>
          No overrides
        </Badge>,
        formatLastChangeNote(item.mobilistReserveNote, item.mobilistAdBlockNote),
        'no-overrides-note'
      )
    );
  }
  return <div className="vap-status-badges">{badges}</div>;
}

function toRow(item: VehicleListItem): RowData {
  return {
    id: item.id,
    data: [
      item.id,
      item.komNr ?? '—',
      item.licensePlate ?? '—',
      vehicleCell(item),
      item.dealerName ?? '—',
      dealerStatusBadge(item),
      mobilistStatusBadges(item),
      <VehicleActions key={`actions-${item.id}`} vehicle={item} />
    ]
  };
}

export function VehicleResultsTable({ items }: Props): ReactElement {
  return (
    <div className="vap-results-table">
      <Table data={{ columns: COLUMNS, rows: items.map(toRow) }} />
    </div>
  );
}
